'use client';

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import {
  GLOBAL_BASE_Z_INDEX,
  type PopupStackEntry,
  RANGE_SIZE,
  usePopupStackContextOptional,
} from '../context/PopupStackContext';

/**
 * Return type for usePopupStack hook
 */
export interface UsePopupStackReturn {
  /** The z-index to use for this popup (center of its range) */
  zIndex: number;
  /** The popup's unique ID in the stack (null if context unavailable) */
  popupId: string | null;
  /**
   * Get a z-index for a child element relative to this popup
   * @param position - Whether the child should be behind or in front of this popup
   */
  getChildZIndex: (position: 'behind' | 'front') => number;
}

/** Default z-index when no context or entry is available */
const DEFAULT_Z_INDEX = GLOBAL_BASE_Z_INDEX + RANGE_SIZE / 2;

/**
 * Hook for top-level popups to register with the popup stack.
 *
 * Registers the popup when `isOpen` is true and unregisters when it becomes false.
 * Provides the z-index for the popup and a function to get z-indexes for
 * child elements.
 *
 * If used outside of a PopupStackProvider, falls back to default z-index values.
 *
 * @param onClose - Callback to close this popup (used for cascading closes when parent closes)
 * @param isOpen - Whether the popup is currently open (controls registration)
 * @returns Object with zIndex, popupId, and getChildZIndex function
 *
 * @example
 * ```tsx
 * function MyPopup({ open, onClose }) {
 *   const { zIndex, getChildZIndex } = usePopupStack(onClose, open);
 *
 *   if (!open) return null;
 *
 *   return (
 *     <Portal>
 *       <div style={{ zIndex }}>
 *         <MainContent />
 *         <SubPanel style={{ zIndex: getChildZIndex('behind') }} />
 *       </div>
 *     </Portal>
 *   );
 * }
 * ```
 */
export function usePopupStack(onClose: () => void, isOpen: boolean): UsePopupStackReturn {
  const context = usePopupStackContextOptional();
  // Use state instead of ref to trigger re-render when entry is registered
  const [entry, setEntry] = useState<PopupStackEntry | null>(null);
  const onCloseRef = useRef(onClose);

  // Keep onClose ref updated without triggering re-registration
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Register when open, unregister when closed
  // Use useLayoutEffect to ensure registration happens synchronously before paint
  useLayoutEffect(() => {
    if (!context || !isOpen) {
      // Clear entry when closed
      setEntry(null);
      return;
    }

    // Wrap onClose to use latest reference
    const wrappedOnClose = () => {
      onCloseRef.current();
    };

    const newEntry = context.register(wrappedOnClose);
    setEntry(newEntry);

    return () => {
      context.unregister(newEntry.id);
    };
  }, [context, isOpen]);

  const getChildZIndex = useCallback(
    (position: 'behind' | 'front'): number => {
      if (context && entry) {
        return context.getChildZIndex(entry.id, position);
      }

      // Fallback when no context - use default offsets
      const offset = position === 'behind' ? -5 : 5;
      return DEFAULT_Z_INDEX + offset;
    },
    [context, entry],
  );

  // Calculate z-index (fallback if no context)
  const zIndex = entry?.centerIndex ?? DEFAULT_Z_INDEX;

  return {
    zIndex,
    popupId: entry?.id ?? null,
    getChildZIndex,
  };
}

export default usePopupStack;
